<template>
  <form-base :teleport-target="dialogTeleportTarget">
    <template #form>
      <v-form ref="form" class="pa-4">
        <v-btn-toggle
          v-if="isPreparationAppointmentsEnabled && !isEditing"
          :model-value="loadTypeLocal.type"
          @update:model-value="setLoadTypeType"
          data-testid="load-type-type-toggle"
          :aria-label="t('loadType.forms.loadTypeForm.labels.type')"
          density="compact"
          color="secondary"
          mandatory
          divided>
          <v-btn
            v-for="option in loadTypeTypeOptions"
            :key="option.value"
            class="border"
            :value="option.value"
            :data-testid="`load-type-type-toggle-${option.value}`">
            {{ option.title }}
          </v-btn>
        </v-btn-toggle>
        <div v-if="isPreparationAppointmentsEnabled && selectedTypeOption" class="pt-8 mb-8">
          <p class="typography-heading-md-bold mb-1" data-testid="load-type-type-heading">
            {{ selectedTypeOption.heading }}
          </p>
          <p class="typography-body-sm">{{ selectedTypeOption.description }}</p>
        </div>
        <v-row>
          <v-col class="required">
            <label for="load-type-name-input" class="typography-body-sm-bold d-block mb-2">
              {{ t('forms.common.labels.name') }}
            </label>
            <text-field
              id="load-type-name-input"
              data-testid="load-type-name-field"
              variant="outlined"
              hide-details="auto"
              :required="true"
              type="text"
              density="compact"
              v-model="loadTypeLocal.name"
              :placeholder="t('loadType.forms.loadTypeForm.placeholders.name')"
              :rules="$validator.rules.required('Load Type Name')"></text-field>
          </v-col>
          <v-col v-if="!isPreparationType" class="required">
            <label for="load-type-operation-input" class="typography-body-sm-bold d-block mb-2">
              {{ t('loadType.forms.loadTypeForm.labels.operation') }}
            </label>
            <v-select
              id="load-type-operation-input"
              data-testid="load-type-operation-select"
              variant="outlined"
              hide-details="auto"
              density="compact"
              v-model="loadTypeLocal.operation"
              required
              :rules="$validator.rules.required('Operation')"
              :items="loadTypeOperationOptions"
              :item-props="optionItemProps"></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="required">
            <label for="load-type-direction-input" class="typography-body-sm-bold d-block mb-2">
              {{ t('loadType.forms.loadTypeForm.labels.direction') }}
            </label>
            <v-select
              id="load-type-direction-input"
              data-testid="load-type-direction-select"
              variant="outlined"
              hide-details="auto"
              density="compact"
              v-model="loadTypeLocal.direction"
              :items="loadTypeDirectionOptions"
              :item-props="optionItemProps"
              required
              :rules="$validator.rules.required('Load Type Direction')"></v-select>
          </v-col>
          <v-col class="required">
            <label for="load-type-equipment-input" class="typography-body-sm-bold d-block mb-2">
              {{ t('loadType.forms.loadTypeForm.labels.equipmentType') }}
            </label>
            <v-select
              id="load-type-equipment-input"
              data-testid="load-type-equipment-select"
              variant="outlined"
              hide-details="auto"
              density="compact"
              v-model="loadTypeLocal.equipmentType"
              required
              :rules="$validator.rules.required('Equipment Type')"
              :items="loadTypeEquipmentTypeOptions"></v-select>
          </v-col>
          <v-col class="required">
            <label
              for="load-type-transportation-mode-input"
              class="typography-body-sm-bold d-block mb-2">
              {{ t('loadType.forms.loadTypeForm.labels.transporationMode') }}
            </label>
            <v-select
              id="load-type-transportation-mode-input"
              density="compact"
              data-testid="load-type-transportation-mode-select"
              variant="outlined"
              hide-details="auto"
              v-model="loadTypeLocal.transportationMode"
              required
              :rules="$validator.rules.required('Transportation Mode')"
              :items="loadTypeTransportationModeOptions"></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <label for="load-type-description-input" class="typography-body-sm-bold d-block mb-2">
              {{ t('forms.common.labels.description') }}
            </label>
            <v-textarea
              id="load-type-description-input"
              data-testid="load-type-description-textarea"
              v-model="loadTypeLocal.description"
              :placeholder="t('loadType.forms.loadTypeForm.placeholders.description')"
              variant="outlined"
              density="compact"
              rows="3"
              no-resize
              hide-details="auto"></v-textarea>
          </v-col>
        </v-row>
        <load-type-duration v-model="loadTypeLocal.duration_min"></load-type-duration>
        <template v-if="!isPreparationType">
          <v-row class="mt-6">
            <v-col class="pb-0">
              <h3 class="mt-6 text-black typography-heading-md-bold">
                {{ t('loadType.forms.loadTypeForm.headers.schedulingPortalAvailability') }}
              </h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-2">
              <carrier-scheduling-toggle
                entity="Load Type"
                :disabled="!$rolePermissions.canCreateLoadType"
                v-model="loadTypeLocal.allowCarrierScheduling" />
            </v-col>
          </v-row>
        </template>
        <template v-if="!omitAdvancedSettings">
          <v-row>
            <v-col class="pb-0">
              <h3 class="mt-6 text-black typography-heading-md-bold">
                {{ t('loadType.forms.loadTypeForm.headers.advancedSettings') }}
              </h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="pt-4">
              <settings-expansion-panels
                :entity-key="DatabaseEntities.LoadType"
                :entity-parent="warehouse ? DatabaseEntities.Warehouse : DatabaseEntities.Org"
                :should-be-expanded="false"
                :excluded-setting-groups="isPreparationType ? [SettingGroups.DOCK_SELECTION] : []"
                @update-settings="updateSettings"
                :entity-settings="loadTypeSettings">
                <template v-slot:append-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-title ripple>
                      {{ t('loadType.forms.loadTypeForm.headers.loadTypeSchedule') }}
                    </v-expansion-panel-title>
                    <v-divider></v-divider>
                    <v-expansion-panel-text>
                      <v-row>
                        <v-col class="mt-6">
                          <time-select-grid
                            :settings-entity="warehouse ?? $org"
                            ref="timeSelect"
                            @pending-changes="hasPendingScheduleChanges = $event"
                            :toggle-to-edit="false"
                            compact
                            v-model="loadTypeLocal.schedule"
                            :with-copy-from-dock="false"
                            :with-copy-from-warehouse="false"
                            @set-schedule-default="setToDefault(loadTypeLocal)"
                            @set-schedule-closed="clearSchedule(loadTypeLocal)"></time-select-grid>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </template>
              </settings-expansion-panels>
            </v-col>
          </v-row>
        </template>
        <v-row class="my-4" v-else>
          <v-col>
            <v-icon size="18" color="gray darken-3" class="mr-1 mb-1">mdi-information</v-icon>
            <span class="text-grey-darken-3 font-size-medium">
              {{
                t(
                  'loadType.forms.loadTypeForm.messages.otherAdvancedSettingsCanBeFoundOnTheLoadTypesPage'
                )
              }}.
            </span>
          </v-col>
        </v-row>
      </v-form>
    </template>

    <template #form-actions>
      <action-group
        @cancel="$emit('close')"
        :confirm-icon="null"
        :disable-confirm="isEditing && !hasChanges"
        :class="actionsTeleportTarget ? 'pa-0' : 'mb-2'"
        :cancel-icon="null"
        :confirm-label="t('loadType.forms.loadTypeForm.buttons.saveLoadType')"
        @confirm="promptForSubmission"></action-group>
    </template>
  </form-base>
</template>

<script>
import {
  DatabaseEntities,
  getLoadTypeDirectionDisplay,
  json,
  LOAD_TYPE_ALLOWED_VALUES_BY_TYPE,
  LoadTypeDirection,
  LoadTypeEquipmentType,
  LoadTypeOperation,
  LoadTypeTransportationMode,
  LoadTypeType,
  MAX_LOAD_TYPES_PER_WAREHOUSE,
  SettingGroups,
  t
} from '@nova/core';
import customMessageMixin from '@satellite/components/mixins/customMessageMixin';
import scheduleMixin from '@satellite/components/mixins/scheduleMixin';
import { usePreparationAppointmentsFeature } from '@satellite/composables/usePreparationAppointmentsFeature';
import { useLoadTypeDirectionLabel } from '@satellite/composables/useLoadTypeDirectionLabel';
import { camelCase } from 'lodash';
import { inject } from 'vue';

export default {
  mixins: [scheduleMixin],
  props: {
    warehouse: {
      type: Object,
      required: false
    },
    loadType: {
      type: Object,
      required: false
    },
    omitAdvancedSettings: {
      type: Boolean,
      default: false
    },
    skipDirtyValidation: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    actionsTeleportTarget: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      DatabaseEntities,
      SettingGroups,
      loadTypeLocal: {},
      formInitialized: false,
      hasPendingScheduleChanges: false,
      initialFormSnapshot: null,
      interval: 30
    };
  },
  computed: {
    loadTypeDirectionOptions() {
      const options = this.buildOptions(LoadTypeDirection, 'direction').map(option => ({
        ...option,
        title:
          this.getDirectionLabel({
            direction: option.value,
            operation: this.loadTypeLocal.operation
          }) ?? option.title
      }));
      return this.filterOptionsByType(
        this.withOptionDescriptions(options, LoadTypeDirection, 'direction').map(option => ({
          ...option,
          subtitle: this.getDropDirectionDescription(option.value) ?? option.subtitle
        })),
        'direction'
      );
    },
    loadTypeOperationOptions() {
      return this.filterOptionsByType(
        this.withOptionDescriptions(
          this.buildOptions(LoadTypeOperation, 'operation'),
          LoadTypeOperation,
          'operation'
        ),
        'operation'
      );
    },
    loadTypeEquipmentTypeOptions() {
      return this.filterOptionsByType(
        this.buildOptions(LoadTypeEquipmentType, 'equipmentType'),
        'equipmentType'
      );
    },
    loadTypeTransportationModeOptions() {
      return this.filterOptionsByType(
        this.buildOptions(LoadTypeTransportationMode, 'transportationMode'),
        'transportationMode'
      );
    },
    loadTypeTypeOptions() {
      return this.buildOptions(LoadTypeType, 'type').map(option => ({
        ...option,
        heading: this.t(`loadType.forms.loadTypeForm.headings.type.${camelCase(option.value)}`),
        description: this.t(
          `loadType.forms.loadTypeForm.descriptions.type.${camelCase(option.value)}`
        )
      }));
    },
    selectedTypeOption() {
      return this.loadTypeTypeOptions.find(option => option.value === this.loadTypeLocal.type);
    },
    isEditing() {
      return Boolean(this.loadType?.id);
    },
    isPreparationType() {
      return this.loadTypeLocal.type === LoadTypeType.Preparation;
    },
    hasChanges() {
      if (this.initialFormSnapshot === null) {
        return false;
      }
      return (
        json.safeStringify(this.loadTypeLocal) !== this.initialFormSnapshot ||
        this.hasPendingScheduleChanges
      );
    },
    loadTypeSettings() {
      return this.loadTypeLocal.settings ?? {};
    },
    maxLoadtypeWarning() {
      const maxLoadTypes = MAX_LOAD_TYPES_PER_WAREHOUSE;
      const isWarehouseLoadType = Boolean(this.warehouse);
      return customMessageMixin.computed.maxLoadTypeLimitWarning(maxLoadTypes, isWarehouseLoadType);
    },
    durationConfirmationChangeBody() {
      return `${this.t(
        'loadType.forms.loadTypeForm.messages.areYouSureYouWantToChangeThisLoadTypes'
      )} <strong><em>${this.t('global.basicTerms.duration').toLowerCase()}</em></strong>? ${this.t(
        'loadType.forms.loadTypeForm.messages.doingThisWillNotChangeAnyExistingAppointmentsDurationThisWillNowBeTheDefaultDurationForAnyNewlyCreatedAppointmentAfterThisPoint'
      )}.
              <br/><br/>
              <strong>${this.t('global.basicTerms.notes')}:</strong> ${this.t(
                'loadType.forms.loadTypeForm.messages.thisWillImpactAllWarehousesUsingThisLoadType'
              )}.`;
    },
    submitAction() {
      return this.loadType?.id ? 'LoadTypes/updateLoadType' : 'LoadTypes/createLoadType';
    }
  },
  methods: {
    t,
    buildOptions(enumObject, group) {
      return Object.entries(enumObject).map(([key, value]) => ({
        value,
        title: this.t(`loadType.forms.loadTypeForm.options.${group}.${camelCase(key)}`)
      }));
    },
    withOptionDescriptions(options, enumObject, group) {
      const keysByValue = Object.fromEntries(
        Object.entries(enumObject).map(([key, value]) => [value, camelCase(key)])
      );
      return options.map(option => ({
        ...option,
        subtitle: this.t(
          `loadType.forms.loadTypeForm.descriptions.${group}.${keysByValue[option.value]}`
        )
      }));
    },
    getDropDirectionDescription(direction) {
      if (
        !this.isPreparationAppointmentsEnabled ||
        this.loadTypeLocal.operation !== LoadTypeOperation.Drop
      ) {
        return null;
      }
      const display = getLoadTypeDirectionDisplay({ direction, operation: LoadTypeOperation.Drop });
      return this.t(`loadType.forms.loadTypeForm.descriptions.direction.${display}`);
    },
    optionItemProps(item) {
      return { title: item.title, subtitle: item.subtitle, lines: 'two' };
    },
    filterOptionsByType(options, field) {
      const allowedValues =
        LOAD_TYPE_ALLOWED_VALUES_BY_TYPE[this.loadTypeLocal.type ?? LoadTypeType.Standard]?.[field];
      return allowedValues
        ? options.filter(option => allowedValues.includes(option.value))
        : options;
    },
    setLoadTypeType(type) {
      if (this.isEditing || !type || type === this.loadTypeLocal.type) {
        return;
      }
      this.loadTypeLocal.type = type;
      Object.entries(LOAD_TYPE_ALLOWED_VALUES_BY_TYPE[type]).forEach(([field, allowedValues]) => {
        if (this.loadTypeLocal[field] && !allowedValues.includes(this.loadTypeLocal[field])) {
          this.loadTypeLocal[field] = field === 'direction' ? '' : null;
        }
      });
      const isPreparation = type === LoadTypeType.Preparation;
      if (isPreparation) {
        this.loadTypeLocal.operation = LoadTypeOperation.Drop;
      }
      this.loadTypeLocal.allowCarrierScheduling = !isPreparation;
    },
    updateSettings(settings) {
      this.loadTypeLocal.settings = settings;
    },
    getChangedFields() {
      let fields = [];

      const isDifferent = (a, b) =>
        typeof a === 'object' || typeof b === 'object'
          ? JSON.stringify(a) !== JSON.stringify(b)
          : a !== b;

      if (this.loadType) {
        Object.keys(this.loadType).forEach(key => {
          if (isDifferent(this.loadType[key], this.loadTypeLocal[key])) {
            fields.push(key);
          }
        });
      } else {
        Object.keys(this.loadTypeLocal).forEach(key => {
          if (typeof this.loadTypeLocal[key] !== 'undefined' && this.loadTypeLocal[key] !== null) {
            fields.push(key);
          }
        });
      }

      return fields;
    },
    submit() {
      this.$refs.timeSelect?.saveChanges();
      this.$store
        .dispatch(this.submitAction, this.loadTypeLocal)
        .then(response => {
          if (response?.data) {
            this.$emit('saved', response.data);

            this.setDirtyStatus(false);

            this.$emit('close');
          }
        })
        .catch(e => {
          if (e.status === 500) {
            this.notify(`Error when saving loadtype`, 'error');
          }
        });
    },
    async promptForSubmission() {
      if (await this.$isFormValid(this.$refs.form)) {
        if (this.loadType?.id && this.loadTypeLocal.duration_min !== this.loadType?.duration_min) {
          this.$confirm(this.durationConfirmationChangeBody, {
            color: 'warning'
          }).then(confirmed => {
            if (confirmed) {
              this.submit();
            }
          });
        } else {
          this.submit();
        }
      }
    }
  },
  watch: {
    loadTypeLocal: {
      handler() {
        if (!this.skipDirtyValidation) {
          this.setDirtyStatus(this.formInitialized);
        }
      },
      deep: true
    }
  },
  beforeMount() {
    this.loadTypeLocal = this.loadType
      ? { ...this.loadType }
      : {
          name: '',
          description: '',
          direction: '',
          duration_min: 15,
          allowCarrierScheduling: true,
          warehouseId: this.warehouse?.id,
          schedule: null,
          type: LoadTypeType.Standard
        };
  },
  async mounted() {
    setTimeout(() => {
      this.formInitialized = true;
    }, 1000);
    await this.$nextTick();
    this.initialFormSnapshot = json.safeStringify(this.loadTypeLocal);
  },
  setup(props) {
    const dialogTeleportTarget = props.compact
      ? inject('dialogActionsRef')
      : props.actionsTeleportTarget;
    const { isPreparationAppointmentsEnabled } = usePreparationAppointmentsFeature();
    const { getDirectionLabel } = useLoadTypeDirectionLabel();
    return { dialogTeleportTarget, getDirectionLabel, isPreparationAppointmentsEnabled };
  }
};
</script>
